import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { ProductCard } from "@/components/ProductCard";
import { CheckoutModal } from "@/components/CheckoutModal";
import { Seo } from "@/components/Seo";
import { API } from "@/lib/api";

export default function Shop({
  fixedKind = "",
  pageTitle = "Shop All Products",
  pageSubtitle = "Printable PDF workbooks and worksheets for little learners aged 3-6.",
}) {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [sort, setSort] = useState("featured");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => setCategories(r.data.categories)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (fixedKind) params.set("kind", fixedKind);
    if (q) params.set("q", q);
    if (sort) params.set("sort", sort);
    axios.get(`${API}/products?${params}`).then((r) => setProducts(r.data.products)).catch(() => {});
  }, [category, q, sort, fixedKind]);

  const showSections = !q && !category && sort === "featured";
  const featured = products.filter((p) => p.is_featured);
  const fresh = products.filter((p) => p.is_new);

  return (
    <div data-testid="shop-page" className="mx-auto max-w-6xl px-6 py-14">
      <Seo title={`${pageTitle} | Divine Kids Learning`} description={pageSubtitle} />

      <p className="font-display text-sm font-bold uppercase tracking-[0.3em] text-brand-purple">
        Divine Kids Learning Store
      </p>
      <h1 className="mt-3 font-display text-4xl font-bold tracking-tight text-slate-800 sm:text-5xl">
        {pageTitle}
      </h1>
      <p className="mt-3 max-w-2xl text-lg font-semibold text-slate-500">{pageSubtitle}</p>

      <div className="mt-10 flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="shop-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search workbooks and worksheets..."
            className="h-14 w-full rounded-full border-2 border-slate-200 bg-white pl-12 pr-5 text-lg font-semibold text-slate-800 outline-none focus:border-sky-400"
          />
        </div>
        <div className="relative">
          <SlidersHorizontal className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <select
            data-testid="shop-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="h-14 w-full appearance-none rounded-full border-2 border-slate-200 bg-white pl-12 pr-10 font-display text-base font-bold text-slate-700 outline-none focus:border-sky-400 sm:w-auto"
          >
            <option value="featured">Featured</option>
            <option value="newest">Newest</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
          </select>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2.5" data-testid="category-filters">
        <button
          data-testid="filter-all"
          onClick={() => setCategory("")}
          className={`rounded-full px-5 py-2.5 font-display text-sm font-bold transition-colors ${
            !category ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-sky-100"
          }`}
        >
          All
        </button>
        {categories.map((c) => (
          <button
            key={c.slug}
            data-testid={`filter-${c.slug}`}
            onClick={() => setCategory(c.slug)}
            className={`rounded-full px-5 py-2.5 font-display text-sm font-bold transition-colors ${
              category === c.slug ? "text-white" : "bg-white text-slate-600 hover:bg-sky-100"
            }`}
            style={category === c.slug ? { backgroundColor: c.color } : {}}
          >
            {c.label}
          </button>
        ))}
      </div>

      {showSections && fresh.length > 0 && (
        <section className="mt-14">
          <h2 className="font-display text-2xl font-bold text-slate-800">New Products</h2>
          <div className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-2">
            {fresh.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} onBuy={setSelected} />
            ))}
          </div>
        </section>
      )}

      <section className="mt-14">
        {showSections && featured.length > 0 && (
          <h2 className="font-display text-2xl font-bold text-slate-800">Featured Products</h2>
        )}
        <div className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-2">
          {(showSections ? featured.length ? featured : products : products).map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} onBuy={setSelected} />
          ))}
        </div>
        {products.length === 0 && (
          <div
            data-testid="shop-empty-state"
            className="mt-6 rounded-[2rem] bg-white p-14 text-center shadow-xl shadow-blue-900/5"
          >
            <p className="font-display text-2xl font-bold text-slate-700">No products here yet</p>
            <p className="mt-2 font-semibold text-slate-500">
              New worksheets and workbooks are coming soon — check back shortly!
            </p>
          </div>
        )}
      </section>

      <CheckoutModal product={selected} open={!!selected} onClose={() => setSelected(null)} />
    </div>
  );
}
